(() => {
  "use strict";

  const ROOT_SELECTOR = "[data-armzone-site]";

  const qs = (selector, scope = document) => scope.querySelector(selector);
  const qsa = (selector, scope = document) =>
    Array.from(scope.querySelectorAll(selector));

  const shopifyRoot = () => {
    const root =
      window.Shopify &&
      window.Shopify.routes &&
      window.Shopify.routes.root
        ? window.Shopify.routes.root
        : "/";

    return root.endsWith("/") ? root : `${root}/`;
  };

  const shopifyURL = (path) =>
    `${shopifyRoot()}${String(path).replace(/^\/+/, "")}`;

  const escapeHTML = (value = "") =>
    String(value)
      .replace(/&/g, "&amp;")
      .replace(/</g, "&lt;")
      .replace(/>/g, "&gt;")
      .replace(/"/g, "&quot;")
      .replace(/'/g, "&#039;");

  const formatMoney = (cents) => {
    const amount = (Number(cents) || 0) / 100;
    const currency =
      window.Shopify &&
      window.Shopify.currency &&
      window.Shopify.currency.active
        ? window.Shopify.currency.active
        : "USD";

    try {
      return new Intl.NumberFormat(
        document.documentElement.lang || "en-US",
        {
          style: "currency",
          currency,
        }
      ).format(amount);
    } catch (error) {
      return `$${amount.toFixed(2)}`;
    }
  };

  const fetchJSON = async (url, options = {}) => {
    const response = await fetch(url, {
      credentials: "same-origin",
      ...options,
      headers: {
        Accept: "application/json",
        ...(options.body ? { "Content-Type": "application/json" } : {}),
        ...(options.headers || {}),
      },
    });

    if (!response.ok) {
      let message = `Request failed (${response.status})`;

      try {
        const data = await response.json();
        message = data.description || data.message || message;
      } catch (error) {
        /* keep default */
      }

      throw new Error(message);
    }

    return response.json();
  };

  const initArmzone = (site) => {
    if (!site || site.dataset.armzoneInitialized === "true") return;
    site.dataset.armzoneInitialized = "true";

    /* =========================================================
       SCROLL PROGRESS
    ========================================================= */

    const progressBar = qs("[data-scroll-progress]", site);

    if (progressBar) {
      const updateProgress = () => {
        const scrollTop =
          window.pageYOffset ||
          document.documentElement.scrollTop ||
          0;

        const max =
          document.documentElement.scrollHeight -
          window.innerHeight;

        const progress =
          max > 0
            ? Math.min(100, Math.max(0, (scrollTop / max) * 100))
            : 0;

        progressBar.style.width = `${progress}%`;
      };

      updateProgress();

      window.addEventListener("scroll", updateProgress, {
        passive: true,
      });

      window.addEventListener("resize", updateProgress, {
        passive: true,
      });
    }

    /* =========================================================
       REVEAL
    ========================================================= */

    const revealElements = qsa(".reveal", site);

    if ("IntersectionObserver" in window) {
      const observer = new IntersectionObserver(
        (entries, revealObserver) => {
          entries.forEach((entry) => {
            if (!entry.isIntersecting) return;
            entry.target.classList.add("visible");
            revealObserver.unobserve(entry.target);
          });
        },
        {
          threshold: 0.08,
          rootMargin: "0px 0px -30px 0px",
        }
      );

      revealElements.forEach((element) => observer.observe(element));
    } else {
      revealElements.forEach((element) =>
        element.classList.add("visible")
      );
    }

    /* =========================================================
       HERO PARALLAX
    ========================================================= */

    const hero = qs("[data-hero]", site);

    if (
      hero &&
      window.matchMedia &&
      window.matchMedia("(pointer: fine)").matches
    ) {
      const resetHero = () => {
        hero.style.setProperty("--hero-x", "0px");
        hero.style.setProperty("--hero-y", "0px");
        hero.style.setProperty("--hero-r", "0deg");
      };

      hero.addEventListener("pointermove", (event) => {
        const rect = hero.getBoundingClientRect();
        const x = (event.clientX - rect.left) / rect.width - 0.5;
        const y = (event.clientY - rect.top) / rect.height - 0.5;

        hero.style.setProperty("--hero-x", `${x * 18}px`);
        hero.style.setProperty("--hero-y", `${y * 13}px`);
        hero.style.setProperty("--hero-r", `${x * 1.6}deg`);
      });

      hero.addEventListener("pointerleave", resetHero);
    }

    /* =========================================================
       CULTURE CARDS
    ========================================================= */

    const cultureCards = qsa("[data-culture-card]", site);
    const cultureFeature = qs("[data-culture-feature]", site);

    if (cultureCards.length && cultureFeature) {
      const featureImage = qs(
        "[data-culture-feature-image]",
        cultureFeature
      );
      const featureLocation = qs(
        "[data-culture-location]",
        cultureFeature
      );
      const featureCoordinate = qs(
        "[data-culture-coordinate]",
        cultureFeature
      );
      const featureKicker = qs(
        "[data-culture-kicker]",
        cultureFeature
      );
      const featureHeadline = qs(
        "[data-culture-headline]",
        cultureFeature
      );
      const featureCopy = qs(
        "[data-culture-copy]",
        cultureFeature
      );
      const featureName = qs(
        "[data-culture-name]",
        cultureFeature
      );
      const featureSymbol = qs(
        ".culture-feature-symbol",
        cultureFeature
      );

      const setCulture = (card, index) => {
        if (!card) return;

        cultureCards.forEach((item, itemIndex) => {
          item.classList.toggle("active", itemIndex === index);
        });

        const accent = card.dataset.accent || "#c09a6b";
        const code = card.dataset.code || "";
        const headline = card.dataset.headline || "";
        const copy = card.dataset.copy || "";
        const location = card.dataset.location || "";
        const coordinate = card.dataset.coordinate || "";
        const scene = card.dataset.scene || "";
        const name = card.dataset.name || "";

        cultureFeature.style.setProperty(
          "--culture-accent",
          accent
        );

        if (scene && featureImage) {
          featureImage.style.backgroundImage = `url("${scene}")`;
        }

        if (featureLocation) {
          featureLocation.textContent = location;
        }

        if (featureCoordinate) {
          featureCoordinate.textContent = coordinate;
        }

        if (featureKicker) {
          featureKicker.textContent =
            `${String(index + 1).padStart(2, "0")} / ${code}`;
        }

        if (featureHeadline) {
          featureHeadline.textContent = headline;
        }

        if (featureCopy) {
          featureCopy.textContent = copy;
        }

        if (featureName) {
          featureName.textContent = name;
        }

        if (featureSymbol) {
          const svg = qs(".culture-icon-seal svg", card);

          if (svg) {
            featureSymbol.innerHTML = svg.outerHTML;
          }
        }
      };

      cultureCards.forEach((card, index) => {
        card.addEventListener("click", () => {
          setCulture(card, index);
        });
      });

      const initialIndex = Math.max(
        0,
        cultureCards.findIndex((card) =>
          card.classList.contains("active")
        )
      );

      setCulture(cultureCards[initialIndex] || cultureCards[0], initialIndex);
    }

    /* =========================================================
       SHOP FILTERS
    ========================================================= */

    const filterButtons = qsa("[data-filter]", site);
    const products = qsa("[data-product]", site);

    const arrangeVisibleProducts = () => {
      let visibleIndex = 0;

      products.forEach((product) => {
        product.classList.remove(
          "product-1",
          "product-2",
          "product-3",
          "product-4"
        );

        if (!product.hidden) {
          visibleIndex += 1;
          product.classList.add(
            `product-${((visibleIndex - 1) % 4) + 1}`
          );
        }
      });
    };

    const setFilter = (value) => {
      const selected = String(value || "ALL").toUpperCase();

      filterButtons.forEach((button) => {
        button.classList.toggle(
          "active",
          String(button.dataset.filter || "").toUpperCase() ===
            selected
        );
      });

      products.forEach((product) => {
        const category = String(
          product.dataset.category || ""
        ).toUpperCase();

        product.hidden =
          selected !== "ALL" &&
          category !== selected;
      });

      arrangeVisibleProducts();
    };

    filterButtons.forEach((button) => {
      button.addEventListener("click", () => {
        setFilter(button.dataset.filter);
      });
    });

    arrangeVisibleProducts();

    /* =========================================================
       DRAWERS
    ========================================================= */

    const overlay = qs("[data-drawer-overlay]", site);
    const menuDrawer = qs("[data-menu-drawer]", site);
    const bagDrawer = qs("[data-bag-drawer]", site);

    const lockPage = () => {
      document.documentElement.style.overflow = "hidden";
      document.body.style.overflow = "hidden";
    };

    const unlockPage = () => {
      document.documentElement.style.overflow = "";
      document.body.style.overflow = "";
    };

    const closeDrawers = () => {
      menuDrawer?.classList.remove("open");
      bagDrawer?.classList.remove("open");
      overlay?.classList.remove("active");
      unlockPage();
    };

    const openMenu = () => {
      if (!menuDrawer) return;
      bagDrawer?.classList.remove("open");
      menuDrawer.classList.add("open");
      overlay?.classList.add("active");
      lockPage();
    };

    const openBag = () => {
      if (!bagDrawer) return;
      menuDrawer?.classList.remove("open");
      bagDrawer.classList.add("open");
      overlay?.classList.add("active");
      lockPage();
    };

    /* =========================================================
       CART
    ========================================================= */

    const bagBody = qs("[data-bag-body]", site);
    const bagFooter = qs("[data-bag-footer]", site);
    const bagTitle = qs("[data-bag-title]", site);
    const bagTotal = qs("[data-bag-total]", site);
    const bagCount = qs("[data-bag-count]", site);

    const updateCartNumbers = (cart) => {
      if (bagTitle) {
        bagTitle.textContent =
          `${cart.item_count} ${
            cart.item_count === 1 ? "ITEM" : "ITEMS"
          }`;
      }

      if (bagTotal) {
        bagTotal.textContent = formatMoney(cart.total_price);
      }

      if (bagCount) {
        bagCount.textContent = cart.item_count;
        bagCount.style.display =
          cart.item_count > 0 ? "" : "none";
      }
    };

    const renderCart = (cart) => {
      updateCartNumbers(cart);

      if (!bagBody) return;

      if (!cart.items || cart.items.length === 0) {
        bagBody.innerHTML = `
          <div class="empty-bag">
            <span>00 / BAG</span>
            <h2>NOTHING<br>HERE YET.</h2>
            <p>Find the piece that says something.</p>
            <button type="button" data-empty-shop>
              SHOP COLLECTION
            </button>
          </div>
        `;

        if (bagFooter) {
          bagFooter.style.display = "none";
        }

        return;
      }

      if (bagFooter) {
        bagFooter.style.display = "";
      }

      bagBody.innerHTML = cart.items
        .map((item) => {
          const variantTitle =
            item.variant_title &&
            item.variant_title !== "Default Title"
              ? item.variant_title
              : "";

          const image = item.image
            ? `<img src="${escapeHTML(
                item.image
              )}" alt="${escapeHTML(
                item.product_title
              )}" loading="lazy">`
            : `<div></div>`;

          return `
            <div class="bag-item">
              ${image}
              <div>
                <h3>${escapeHTML(item.product_title)}</h3>
                ${
                  variantTitle
                    ? `<span>${escapeHTML(variantTitle)}</span>`
                    : ""
                }
                <strong>${formatMoney(
                  item.final_line_price
                )}</strong>
                <div class="quantity">
                  <button
                    type="button"
                    data-cart-change
                    data-cart-key="${escapeHTML(item.key)}"
                    data-cart-quantity="${Math.max(
                      0,
                      item.quantity - 1
                    )}"
                    aria-label="Decrease quantity"
                  >−</button>

                  <span>${item.quantity}</span>

                  <button
                    type="button"
                    data-cart-change
                    data-cart-key="${escapeHTML(item.key)}"
                    data-cart-quantity="${item.quantity + 1}"
                    aria-label="Increase quantity"
                  >+</button>
                </div>
              </div>
            </div>
          `;
        })
        .join("");
    };

    const loadCart = async () => {
      try {
        const cart = await fetchJSON(shopifyURL("cart.js"));
        renderCart(cart);
        return cart;
      } catch (error) {
        console.error("ARMZONE cart load error:", error);
        return null;
      }
    };

    const addProduct = async (button) => {
      const variantId =
        button.dataset.variant ||
        button.dataset.variantId;

      if (!variantId) return;

      const originalHTML = button.innerHTML;

      button.disabled = true;
      button.textContent = "ADDING";

      try {
        await fetchJSON(shopifyURL("cart/add.js"), {
          method: "POST",
          body: JSON.stringify({
            items: [
              {
                id: Number(variantId),
                quantity: 1,
              },
            ],
          }),
        });

        await loadCart();

        button.textContent = "ADDED";
        openBag();

        window.setTimeout(() => {
          button.innerHTML = originalHTML;
          button.disabled = false;
        }, 800);
      } catch (error) {
        console.error("ARMZONE add-to-cart error:", error);

        button.textContent = "ERROR";

        window.setTimeout(() => {
          button.innerHTML = originalHTML;
          button.disabled = false;
        }, 1100);
      }
    };

    const changeQuantity = async (key, quantity) => {
      try {
        const cart = await fetchJSON(
          shopifyURL("cart/change.js"),
          {
            method: "POST",
            body: JSON.stringify({
              id: key,
              quantity: Math.max(0, Number(quantity) || 0),
            }),
          }
        );

        renderCart(cart);
      } catch (error) {
        console.error("ARMZONE cart update error:", error);
      }
    };

    /* =========================================================
       EVENTS
    ========================================================= */

    site.addEventListener("click", (event) => {
      const menuOpen = event.target.closest("[data-menu-open]");

      if (menuOpen) {
        event.preventDefault();
        openMenu();
        return;
      }

      const bagOpen = event.target.closest("[data-bag-open]");

      if (bagOpen) {
        event.preventDefault();
        openBag();
        return;
      }

      const closeButton = event.target.closest(
        "[data-menu-close], [data-bag-close]"
      );

      if (closeButton) {
        event.preventDefault();
        closeDrawers();
        return;
      }

      const drawerLink = event.target.closest("[data-drawer-link]");

      if (drawerLink) {
        closeDrawers();
        return;
      }

      if (overlay && event.target === overlay) {
        closeDrawers();
        return;
      }

      const addButton = event.target.closest("[data-add-product]");

      if (addButton) {
        event.preventDefault();
        addProduct(addButton);
        return;
      }

      const quantityButton = event.target.closest(
        "[data-cart-change]"
      );

      if (quantityButton) {
        event.preventDefault();

        changeQuantity(
          quantityButton.dataset.cartKey,
          quantityButton.dataset.cartQuantity
        );

        return;
      }

      const emptyShop = event.target.closest("[data-empty-shop]");

      if (emptyShop) {
        event.preventDefault();
        closeDrawers();

        qs("#shop", site)?.scrollIntoView({
          behavior: "smooth",
          block: "start",
        });
      }
    });

    document.addEventListener("keydown", (event) => {
      if (event.key === "Escape") {
        closeDrawers();
      }
    });

    loadCart();
  };

  const initAll = (scope = document) => {
    if (scope.matches && scope.matches(ROOT_SELECTOR)) {
      initArmzone(scope);
    }

    qsa(ROOT_SELECTOR, scope).forEach(initArmzone);
  };

  if (document.readyState === "loading") {
    document.addEventListener("DOMContentLoaded", () => {
      initAll();
    });
  } else {
    initAll();
  }

  document.addEventListener(
    "shopify:section:load",
    (event) => {
      initAll(event.target);
    }
  );
})();